"use client";

import * as React from "react";
import { VariantProps, cva } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { useInboxPanel } from "@/hooks/useInboxPanel";
import { Button } from "@/components/Base/Button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback } from "react";
import { FOOTER_HEIGHT_PX } from "@/lib/layout";

const expandablePanelVariants = cva(
  "fixed flex flex-col bg-background border-border transition-all duration-300 ease-in-out",
  {
    variants: {
      side: {
        left: "left-0 border-r",
        right: "right-0 border-l",
      },
      variant: {
        default: "shadow-lg",
        floating: "shadow-xl rounded-lg border m-2",
        overlay: "shadow-2xl",
      },
      headerless: {
        true: "",
        false: "inset-y-0",
      },
    },
    defaultVariants: {
      side: "right",
      variant: "default",
      headerless: false,
    },
  }
);

export interface ExpandablePanelProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof expandablePanelVariants> {
  /**
   * The side of the screen where the panel should appear
   */
  side?: "left" | "right";

  /**
   * Panel width configuration - number for fixed width, "responsive" for dynamic width
   */
  width?: number | "responsive";

  /**
   * Whether the panel can be collapsed
   */
  collapsible?: boolean;

  /**
   * Whether to show backdrop overlay on mobile
   */
  overlay?: boolean;

  /**
   * Custom header content
   */
  header?: React.ReactNode;

  /**
   * Custom header background class
   */
  headerClassName?: string;

  /**
   * Whether to show close button in header
   */
  showCloseButton?: boolean;

  /**
   * Custom close button content
   */
  closeButtonContent?: React.ReactNode;

  /**
   * Whether to show the panel in headerless mode (positioned below main header)
   */
  headerless?: boolean;

  /**
   * Panel content
   */
  children: React.ReactNode;

  /**
   * Whether panel is currently open
   */
  isOpen?: boolean;

  /**
   * Function to call when panel should be closed
   */
  onClose?: () => void;

  /**
   * Whether panel is currently transitioning
   */
  isTransitioning?: boolean;

  /**
   * Panel width for calculations
   */
  panelWidth?: number;
}

export const ExpandablePanel = React.forwardRef<
  HTMLDivElement,
  ExpandablePanelProps
>(
  (
    {
      side = "right",
      variant = "default",
      width = "responsive",
      collapsible = true,
      overlay = true,
      header,
      headerClassName,
      showCloseButton = true,
      closeButtonContent,
      headerless: headerlessProp,
      children,
      className,
      isOpen: isOpenProp,
      onClose: onCloseProp,
      isTransitioning: isTransitioningProp,
      panelWidth: panelWidthProp,
      ...props
    },
    ref
  ) => {
    const contextValues = useInboxPanel();

    const isOpen = contextValues?.isOpen ?? isOpenProp ?? false;
    const onClose = useCallback(() => {
      const close = contextValues?.closePanel ?? onCloseProp;
      close?.();
    }, [onCloseProp, contextValues?.closePanel]);
    const isTransitioning =
      contextValues?.isTransitioning ?? isTransitioningProp ?? false;
    const panelWidth = contextValues?.panelWidth ?? panelWidthProp ?? 600;
    const isHeaderless = headerlessProp ?? false;
    const headerHeight = contextValues?.headerHeight ?? 80; // Default header height

    // Calculate actual width based on configuration
    const actualWidth = React.useMemo(() => {
      if (width === "responsive") {
        return panelWidth;
      }
      return width;
    }, [width, panelWidth]);

    // Handle keyboard shortcuts
    React.useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape" && isOpen && collapsible) {
          event.preventDefault();
          onClose();
        }
      };

      if (isOpen) {
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
      }
    }, [isOpen, collapsible, onClose]);

    // Handle focus management
    const panelRef = React.useRef<HTMLDivElement>(null);
    const previousFocusRef = React.useRef<HTMLElement | null>(null);

    React.useEffect(() => {
      if (isOpen) {
        // Store the previously focused element
        previousFocusRef.current = document.activeElement as HTMLElement;

        // Focus the panel when it opens
        setTimeout(() => {
          if (panelRef.current) {
            const focusableElement = panelRef.current.querySelector(
              'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            ) as HTMLElement;

            if (focusableElement) {
              focusableElement.focus();
            } else {
              panelRef.current.focus();
            }
          }
        }, 100); // Small delay to ensure transition has started
      } else {
        // Restore focus to previously focused element when panel closes
        setTimeout(() => {
          if (previousFocusRef.current) {
            previousFocusRef.current.focus();
          }
        }, 300); // Wait for transition to complete
      }
    }, [isOpen]);

    // Combine external ref with internal ref
    const setRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        // Set internal ref
        (panelRef as React.MutableRefObject<HTMLDivElement | null>).current =
          node;

        // Set external ref
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
      },
      [ref]
    );

    // Calculate transform based on side and open state
    const transform = React.useMemo(() => {
      if (isOpen) return "translateX(0)";

      if (side === "left") {
        return `translateX(-${actualWidth}px)`;
      } else {
        return `translateX(${actualWidth}px)`;
      }
    }, [isOpen, side, actualWidth]);

    // Backdrop component for mobile overlay
    const Backdrop =
      overlay && isOpen && !isHeaderless ? (
        <div
          className={cn(
            "fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 ease-in-out md:hidden",
            isTransitioning ? "opacity-0" : "opacity-100"
          )}
          onClick={collapsible ? onClose : undefined}
          aria-hidden="true"
        />
      ) : null;

    // Calculate panel positioning for headerless mode
    const panelStyle = React.useMemo(() => {
      const baseStyle = {
        width: `${actualWidth}px`,
        transform,
        willChange: isTransitioning ? "transform" : "auto",
        bottom: `${FOOTER_HEIGHT_PX}px`,
      };

      if (isHeaderless) {
        return {
          ...baseStyle,
          top: `${headerHeight}px`,
        };
      }

      return baseStyle;
    }, [actualWidth, transform, isTransitioning, isHeaderless, headerHeight]);

    return (
      <>
        {Backdrop}
        <div
          ref={setRef}
          className={cn(
            expandablePanelVariants({
              side,
              variant,
              headerless: isHeaderless,
            }),
            className
          )}
          style={panelStyle}
          role={isHeaderless ? "region" : "dialog"}
          aria-modal={isHeaderless ? undefined : "true"}
          aria-label={isHeaderless ? "Inbox panel region" : "Expandable panel"}
          tabIndex={-1}
          {...props}
        >
          {/* Panel Header - hidden in headerless mode */}
          {!isHeaderless && (header || showCloseButton) && (
            <div
              className={cn(
                "flex items-center justify-between border-b border-border px-4 py-3",
                headerClassName || "bg-muted/30"
              )}
            >
              <div className="flex-1">{header}</div>
              {showCloseButton && collapsible && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0"
                  onClick={onClose}
                  aria-label="Close panel"
                >
                  {closeButtonContent ||
                    (side === "left" ? (
                      <ChevronLeft className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    ))}
                </Button>
              )}
            </div>
          )}

          {/* Panel Content */}
          <div className="flex-1 overflow-hidden">{children}</div>
        </div>
      </>
    );
  }
);

ExpandablePanel.displayName = "ExpandablePanel";

export { expandablePanelVariants };
